import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import MetricCard from '@/components/ui/MetricCard'
import LineChart from '@/components/charts/LineChart'
import BarChart from '@/components/charts/BarChart'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

type Tab = 'performance' | 'factors' | 'causal' | 'regimes' | 'var-stress' | 'compare'

const formatPct = (v: number | null) => v != null ? `${(v * 100).toFixed(2)}%` : 'N/A'
const formatNum = (v: number | null, d = 2) => v != null ? v.toFixed(d) : 'N/A'

const WINDOWS = ['full', '10y', '5y', '2y'] as const
const WINDOW_LABELS: Record<string, string> = { full: 'ITD', '10y': '10Y', '5y': '5Y', '2y': '2Y' }

function WindowSelector({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex gap-1">
      {WINDOWS.map(w => (
        <button
          key={w}
          onClick={() => onChange(w)}
          className={`px-3 py-1 text-sm rounded ${
            value === w ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          {WINDOW_LABELS[w]}
        </button>
      ))}
    </div>
  )
}

export default function FundAnalysis() {
  const [tab, setTab] = useState<Tab>('performance')
  const [selectedFund, setSelectedFund] = useState<string>('')
  const [compareFunds, setCompareFunds] = useState<string[]>([])
  const [factorWindow, setFactorWindow] = useState('full')
  const [causalWindow, setCausalWindow] = useState('full')
  const [regimeWindow, setRegimeWindow] = useState('full')
  const [searchTerm, setSearchTerm] = useState('')

  // Load fund list
  const { data: funds } = useQuery({
    queryKey: ['fund-analysis-funds'],
    queryFn: () => api.get('/fund-analysis/funds').then(r => r.data),
  })

  // Performance
  const { data: perf, isLoading: perfLoading } = useQuery({
    queryKey: ['fund-analysis-perf', selectedFund],
    queryFn: () => api.get(`/fund-analysis/performance/${selectedFund}`).then(r => r.data),
    enabled: !!selectedFund && tab === 'performance',
  })

  // Factor model
  const { data: factors, isLoading: factorsLoading } = useQuery({
    queryKey: ['fund-analysis-factors', selectedFund, factorWindow],
    queryFn: () => api.get(`/fund-analysis/factor-model/${selectedFund}?window=${factorWindow}`).then(r => r.data),
    enabled: !!selectedFund && tab === 'factors',
  })

  // Causal graph
  const { data: causal, isLoading: causalLoading } = useQuery({
    queryKey: ['fund-analysis-causal', selectedFund, causalWindow],
    queryFn: () => api.get(`/fund-analysis/causal-graph/${selectedFund}?window=${causalWindow}`).then(r => r.data),
    enabled: !!selectedFund && tab === 'causal',
  })

  // Regime analysis
  const { data: regimes, isLoading: regimesLoading } = useQuery({
    queryKey: ['fund-analysis-regimes', selectedFund, regimeWindow],
    queryFn: () => api.get(`/fund-analysis/regimes/${selectedFund}?window=${regimeWindow}`).then(r => r.data),
    enabled: !!selectedFund && tab === 'regimes',
  })

  // VaR & Stress
  const { data: varStress, isLoading: varLoading } = useQuery({
    queryKey: ['fund-analysis-var', selectedFund],
    queryFn: () => api.get(`/fund-analysis/var-stress/${selectedFund}`).then(r => r.data),
    enabled: !!selectedFund && tab === 'var-stress',
  })

  // Comparison
  const { data: comparison, isLoading: compareLoading } = useQuery({
    queryKey: ['fund-analysis-compare', compareFunds],
    queryFn: () => api.get(`/fund-analysis/compare?fund_ids=${compareFunds.join(',')}`).then(r => r.data),
    enabled: compareFunds.length >= 2 && tab === 'compare',
  })

  // Filtered fund list
  const filteredFunds = useMemo(() => {
    if (!funds) return []
    if (!searchTerm) return funds
    const lower = searchTerm.toLowerCase()
    return funds.filter((f: any) =>
      f.name?.toLowerCase().includes(lower) ||
      f.ticker?.toLowerCase().includes(lower)
    )
  }, [funds, searchTerm])

  const tabs: { key: Tab; label: string }[] = [
    { key: 'performance', label: 'Performance' },
    { key: 'factors', label: 'Factor Model' },
    { key: 'causal', label: 'Causal Graph' },
    { key: 'regimes', label: 'Regime Analysis' },
    { key: 'var-stress', label: 'VaR & Stress' },
    { key: 'compare', label: 'Comparison' },
  ]

  const selectedFundName = funds?.find((f: any) => f.id === selectedFund)?.name || selectedFund

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Fund Risk Analysis</h1>
        <p className="text-muted-foreground">Factor models, regime analysis, VaR, and stress testing</p>
      </div>

      {/* Fund selector */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium mb-1 block">Select Fund</label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search by name or ticker..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="input flex-1"
              />
              <select
                value={selectedFund}
                onChange={e => setSelectedFund(e.target.value)}
                className="input w-96"
              >
                <option value="">-- Select --</option>
                {filteredFunds?.map((f: any) => (
                  <option key={f.id} value={f.id}>
                    {f.name} ({f.ticker}) [{f.source}]
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {!selectedFund && tab !== 'compare' ? (
        <div className="text-center text-muted-foreground py-12">
          Select a fund to begin analysis
        </div>
      ) : (
        <>
          {/* PERFORMANCE TAB */}
          {tab === 'performance' && (
            perfLoading ? <LoadingSpinner /> : perf && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <MetricCard label="Ann. Return" value={formatPct(perf.ann_return)} />
                  <MetricCard label="Ann. Volatility" value={formatPct(perf.ann_volatility)} />
                  <MetricCard label="Sharpe" value={formatNum(perf.sharpe)} />
                  <MetricCard label="Sortino" value={formatNum(perf.sortino)} />
                  <MetricCard label="Max Drawdown" value={formatPct(perf.max_drawdown)} subtitle={perf.max_drawdown_date} />
                  <MetricCard label="Calmar" value={formatNum(perf.calmar)} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <MetricCard label="Best Month" value={formatPct(perf.best_month)} />
                  <MetricCard label="Worst Month" value={formatPct(perf.worst_month)} />
                  <MetricCard label="% Positive Months" value={formatPct(perf.pct_positive_months)} />
                  <MetricCard label="Skewness" value={formatNum(perf.skewness)} />
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="font-medium mb-2">Cumulative Return</h3>
                  <LineChart
                    data={perf.cumulative_chart || []}
                    xKey="date"
                    lines={[{ key: 'value', name: selectedFundName }]}
                    height={350}
                    xTickFormatter={(v: string) => v?.slice(0, 7) || ''}
                    yTickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
                  />
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="font-medium mb-2">Drawdown</h3>
                  <LineChart
                    data={perf.drawdown_chart || []}
                    xKey="date"
                    lines={[{ key: 'value', name: 'Drawdown', color: 'hsl(0, 84%, 60%)' }]}
                    height={250}
                    xTickFormatter={(v: string) => v?.slice(0, 7) || ''}
                    yTickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
                  />
                </div>
              </div>
            )
          )}

          {/* FACTOR MODEL TAB */}
          {tab === 'factors' && (
            <div className="space-y-6">
              <WindowSelector value={factorWindow} onChange={setFactorWindow} />
              {factorsLoading ? <LoadingSpinner /> : factors && !factors.error && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricCard label="R-squared" value={formatNum(factors.r_squared, 3)} subtitle={`${factors.n_observations} obs`} />
                    <MetricCard label="Alpha (ann.)" value={formatPct(factors.alpha_annualized)} subtitle={`t=${formatNum(factors.alpha_t_stat)}`} />
                    <MetricCard label="Alpha Significant" value={factors.alpha_significant ? 'Yes' : 'No'} />
                    <MetricCard label="Significant Factors" value={factors.n_significant} />
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="font-medium mb-3">Factor Exposures (sorted by |t-stat|)</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 px-3">Factor</th>
                            <th className="text-right py-2 px-3">Beta</th>
                            <th className="text-right py-2 px-3">t-stat</th>
                            <th className="text-right py-2 px-3">Correlation</th>
                            <th className="text-left py-2 px-3">Significance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {factors.factors?.map((f: any, i: number) => (
                            <tr key={i} className={`border-b border-border/50 ${Math.abs(f.t_stat || 0) > 1.96 ? 'bg-primary/5' : ''}`}>
                              <td className="py-2 px-3 font-mono text-xs">{f.name}</td>
                              <td className="text-right py-2 px-3">{formatNum(f.beta, 4)}</td>
                              <td className={`text-right py-2 px-3 font-medium ${Math.abs(f.t_stat || 0) > 1.96 ? 'text-primary' : 'text-muted-foreground'}`}>
                                {formatNum(f.t_stat)}
                              </td>
                              <td className="text-right py-2 px-3">{formatNum(f.correlation, 3)}</td>
                              <td className="py-2 px-3">
                                {Math.abs(f.t_stat || 0) > 2.58 ? '***' : Math.abs(f.t_stat || 0) > 1.96 ? '**' : Math.abs(f.t_stat || 0) > 1.64 ? '*' : ''}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
              {factors?.error && <div className="text-destructive">{factors.error}</div>}
            </div>
          )}

          {/* CAUSAL GRAPH TAB */}
          {tab === 'causal' && (
            <div className="space-y-6">
              <WindowSelector value={causalWindow} onChange={setCausalWindow} />
              {causalLoading ? <LoadingSpinner /> : causal && !causal.error && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricCard label="Method" value={causal.method} />
                    <MetricCard label="Observations" value={causal.n_observations} />
                    <MetricCard label="Causal Edges" value={causal.n_edges} />
                    <MetricCard label="Period" value={`${causal.start_date?.slice(0,7)} - ${causal.end_date?.slice(0,7)}`} />
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="font-medium mb-3">Causal Edges (Fund drivers)</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 px-3">Source Factor</th>
                            <th className="text-left py-2 px-3">Target</th>
                            <th className="text-right py-2 px-3">Lag</th>
                            <th className="text-right py-2 px-3">Strength</th>
                            <th className="text-right py-2 px-3">p-value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {causal.edges
                            ?.sort((a: any, b: any) => Math.abs(b.strength || 0) - Math.abs(a.strength || 0))
                            .map((e: any, i: number) => (
                              <tr key={i} className="border-b border-border/50">
                                <td className="py-2 px-3 font-mono text-xs">{e.source}</td>
                                <td className="py-2 px-3">{e.target}</td>
                                <td className="text-right py-2 px-3">{e.lag}</td>
                                <td className={`text-right py-2 px-3 font-medium ${(e.strength || 0) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatNum(e.strength, 3)}
                                </td>
                                <td className="text-right py-2 px-3 text-muted-foreground">
                                  {e.p_value != null ? formatNum(e.p_value, 4) : '-'}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="font-medium mb-3">Factor Nodes (by correlation with fund)</h3>
                    <div className="flex flex-wrap gap-2">
                      {causal.nodes
                        ?.filter((n: any) => n.type === 'factor')
                        .sort((a: any, b: any) => Math.abs(b.correlation || 0) - Math.abs(a.correlation || 0))
                        .map((n: any) => (
                          <div key={n.id} className={`px-3 py-1.5 rounded text-sm border ${
                            Math.abs(n.correlation || 0) > 0.3
                              ? 'bg-primary/10 border-primary/30 font-medium'
                              : 'bg-muted border-border'
                          }`}>
                            {n.id} <span className="text-muted-foreground">({formatNum(n.correlation, 3)})</span>
                          </div>
                        ))}
                    </div>
                  </div>
                </>
              )}
              {causal?.error && <div className="text-destructive">{causal.error}</div>}
            </div>
          )}

          {/* REGIME ANALYSIS TAB */}
          {tab === 'regimes' && (
            <div className="space-y-6">
              <WindowSelector value={regimeWindow} onChange={setRegimeWindow} />
              {regimesLoading ? <LoadingSpinner /> : regimes && (
                Object.entries(regimes)
                  .filter(([key]) => key !== 'window' && key !== 'return_frequency')
                  .map(([key, regime]: [string, any]) => (
                    <div key={key} className="bg-card border border-border rounded-lg p-4">
                      <h3 className="font-medium mb-3 capitalize">{key.replace(/_/g, ' ')}</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-2 px-3">Regime</th>
                              <th className="text-right py-2 px-3">Obs</th>
                              <th className="text-right py-2 px-3">Ann. Return</th>
                              <th className="text-right py-2 px-3">Ann. Vol</th>
                              <th className="text-right py-2 px-3">Sharpe</th>
                              <th className="text-right py-2 px-3">Hit Rate</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(regime)
                              .filter(([k]) => k !== 'threshold')
                              .map(([k, r]: [string, any]) => (
                                <tr key={k} className="border-b border-border/50">
                                  <td className="py-2 px-3">{r.label}</td>
                                  <td className="text-right py-2 px-3">{r.n_obs}</td>
                                  <td className={`text-right py-2 px-3 font-medium ${(r.ann_return || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {formatPct(r.ann_return)}
                                  </td>
                                  <td className="text-right py-2 px-3">{formatPct(r.ann_volatility)}</td>
                                  <td className="text-right py-2 px-3">{formatNum(r.sharpe)}</td>
                                  <td className="text-right py-2 px-3">{formatPct(r.hit_rate)}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))
              )}
            </div>
          )}

          {/* VAR & STRESS TAB */}
          {tab === 'var-stress' && (
            varLoading ? <LoadingSpinner /> : varStress && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="font-medium mb-3">Weekly VaR & CVaR (overlapping)</h3>
                    <table className="w-full text-sm">
                      <tbody>
                        <tr className="border-b border-border/50"><td className="py-2">VaR 95%</td><td className="text-right font-medium">{formatPct(varStress.weekly?.var_95)}</td></tr>
                        <tr className="border-b border-border/50"><td className="py-2">VaR 99%</td><td className="text-right font-medium">{formatPct(varStress.weekly?.var_99)}</td></tr>
                        <tr className="border-b border-border/50"><td className="py-2">CVaR 95%</td><td className="text-right font-medium text-red-600">{formatPct(varStress.weekly?.cvar_95)}</td></tr>
                        <tr><td className="py-2">CVaR 99%</td><td className="text-right font-medium text-red-600">{formatPct(varStress.weekly?.cvar_99)}</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="font-medium mb-3">Monthly VaR & CVaR</h3>
                    <table className="w-full text-sm">
                      <tbody>
                        <tr className="border-b border-border/50"><td className="py-2">VaR 95%</td><td className="text-right font-medium">{formatPct(varStress.monthly?.var_95)}</td></tr>
                        <tr className="border-b border-border/50"><td className="py-2">VaR 99%</td><td className="text-right font-medium">{formatPct(varStress.monthly?.var_99)}</td></tr>
                        <tr className="border-b border-border/50"><td className="py-2">CVaR 95%</td><td className="text-right font-medium text-red-600">{formatPct(varStress.monthly?.cvar_95)}</td></tr>
                        <tr><td className="py-2">CVaR 99%</td><td className="text-right font-medium text-red-600">{formatPct(varStress.monthly?.cvar_99)}</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="font-medium mb-3">Historical Stress Tests</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-3">Event</th>
                          <th className="text-left py-2 px-3">Period</th>
                          <th className="text-right py-2 px-3">Days</th>
                          <th className="text-right py-2 px-3">Fund Return</th>
                          <th className="text-right py-2 px-3">SPX Return</th>
                          <th className="text-right py-2 px-3">Beta to SPX</th>
                        </tr>
                      </thead>
                      <tbody>
                        {varStress.stress_tests?.map((s: any, i: number) => (
                          <tr key={i} className="border-b border-border/50">
                            <td className="py-2 px-3 font-medium">{s.event}</td>
                            <td className="py-2 px-3 text-xs text-muted-foreground">{s.start} to {s.end}</td>
                            <td className="text-right py-2 px-3">{s.n_days}</td>
                            <td className={`text-right py-2 px-3 font-medium ${(s.fund_return || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatPct(s.fund_return)}
                            </td>
                            <td className={`text-right py-2 px-3 ${(s.spx_return || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatPct(s.spx_return)}
                            </td>
                            <td className="text-right py-2 px-3">{formatNum(s.beta_to_spx)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {varStress.return_distribution?.histogram && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="font-medium mb-2">Return Distribution</h3>
                    <BarChart
                      data={varStress.return_distribution.histogram.map((h: any) => ({
                        bin: `${(h.bin_start * 100).toFixed(1)}%`,
                        count: h.count,
                      }))}
                      xKey="bin"
                      bars={[{ key: 'count', name: 'Frequency', color: 'hsl(217, 91%, 30%)' }]}
                      height={250}
                    />
                  </div>
                )}
              </div>
            )
          )}

          {/* COMPARISON TAB */}
          {tab === 'compare' && (
            <div className="space-y-6">
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="font-medium mb-2">Select Funds to Compare</h3>
                <p className="text-xs text-muted-foreground mb-3">Hold Ctrl/Cmd to select multiple funds (min 2)</p>
                <select
                  multiple
                  value={compareFunds}
                  onChange={e => setCompareFunds(Array.from(e.target.selectedOptions, o => o.value))}
                  className="input w-full h-48"
                >
                  {funds?.map((f: any) => (
                    <option key={f.id} value={f.id}>{f.name} ({f.ticker})</option>
                  ))}
                </select>
              </div>
              {compareLoading ? <LoadingSpinner /> : comparison && !comparison.error && (
                <>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="font-medium mb-3">Side-by-Side Metrics</h3>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-3">Fund</th>
                          <th className="text-right py-2 px-3">Ann. Return</th>
                          <th className="text-right py-2 px-3">Ann. Vol</th>
                          <th className="text-right py-2 px-3">Sharpe</th>
                          <th className="text-right py-2 px-3">Max DD</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparison.metrics?.map((m: any, i: number) => {
                          const fund = funds?.find((f: any) => f.id === m.fund_id)
                          return (
                            <tr key={i} className="border-b border-border/50">
                              <td className="py-2 px-3">{fund?.name || m.fund_id}</td>
                              <td className="text-right py-2 px-3">{formatPct(m.ann_return)}</td>
                              <td className="text-right py-2 px-3">{formatPct(m.ann_volatility)}</td>
                              <td className="text-right py-2 px-3 font-medium">{formatNum(m.sharpe)}</td>
                              <td className="text-right py-2 px-3 text-red-600">{formatPct(m.max_drawdown)}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  {comparison.rolling_correlation?.length > 0 && (
                    <div className="bg-card border border-border rounded-lg p-4">
                      <h3 className="font-medium mb-2">Rolling 63-Day Correlation</h3>
                      <LineChart
                        data={comparison.rolling_correlation}
                        xKey="date"
                        lines={[{ key: 'value', name: 'Correlation' }]}
                        height={300}
                        xTickFormatter={(v: string) => v?.slice(0, 7) || ''}
                      />
                    </div>
                  )}
                </>
              )}
              {comparison?.error && <div className="text-destructive">{comparison.error}</div>}
            </div>
          )}
        </>
      )}
    </div>
  )
}
